import { constant, noop } from 'lodash';
import { useCallback, useMemo } from 'react';

import { type PublicClip } from '@air/api';
import { type CardSize, type Clip } from '@air/api/types';
import { type GallerySection } from '@air/component-gallery-view';
import { useBreakpointsContext } from '@air/provider-media-query';
import { type Upload, UploadStatus } from '@air/redux-uploader';

import { type GalleryMetadata } from '~/components/Gallery/galleryMetadata';
import { GalleryItemType, type GalleryViewRender } from '~/components/Gallery/types';
import { UploadingFileGalleryCard } from '~/components/Gallery/UploadingFileGalleryCard';
import { useGallerySectionFooter } from '~/components/Gallery/useGallerySectionFooter';
import {
  type UseGallerySectionHeaderParams,
  useGallerySectionHeader,
} from '~/components/Gallery/useGallerySectionHeader';
import { DragType } from '~/components/Shared/Drag/dragTypes';
import { WKSPC_DESKTOP_HORIZONTAL_PADDING_WITH_SIDENAV_OPEN } from '~/constants/WorkspaceSpacing';
import useCSSGridLikeLayout from '~/hooks/useCSSGridLikeLayout';
import { type UseGalleryDataInfo } from '~/swr-hooks/gallery/types';

const VERTICAL_SPACING_SMALL = 12;
const VERTICAL_SPACING_DEFAULT = 24;
const FILE_CARD_ASPECT_RATIO = 1.25;

export const FileGalleryCardSize: { [key in CardSize]: number } = {
  small: 124,
  medium: 166,
  large: 249,
  'extra-large': 332,
};

export type UseGalleryFilesParams<C> = Pick<UseGallerySectionHeaderParams, 'renderHeader'> & {
  data?: UseGalleryDataInfo<C>;
  containerWidth: number;
  renderFile: ((props: GalleryViewRender<C>) => JSX.Element) | undefined;
  loadMore?: () => void;
  uploads?: Upload[];
  isFirstSection: boolean;
  containerHorizontalPadding?: number;
  headerContainerHeight?: number;
  itemHeight: number;
  itemAdditionalHeight?: number;
  itemHorizontalSpacing?: number;
  itemWidth?: number;
  isSelectable?: (file: C) => boolean;
  rowVerticalSpacing?: number;
};

type FileEntry<C extends Clip | PublicClip> =
  | { type: typeof GalleryItemType.upload; item: Upload }
  | { type: typeof GalleryItemType.file; item: C };

export const useGalleryFiles = <C extends Clip | PublicClip>({
  renderFile,
  containerWidth,
  loadMore = noop,
  data,
  uploads = [],
  isFirstSection,
  containerHorizontalPadding = WKSPC_DESKTOP_HORIZONTAL_PADDING_WITH_SIDENAV_OPEN,
  headerContainerHeight,
  itemHeight,
  itemAdditionalHeight,
  itemHorizontalSpacing = 24,
  itemWidth,
  isSelectable = constant(false),
  renderHeader,
  rowVerticalSpacing,
}: UseGalleryFilesParams<C>): GallerySection<GalleryMetadata> | null => {
  const { isAboveSmallScreen } = useBreakpointsContext();

  const filteredUploads = useMemo(() => {
    const existingClipIds = new Set((data?.items ?? []).map((c) => c.id));

    return uploads.filter((u) => {
      // Clip is already in gallery — real card handles display
      if (existingClipIds.has(u.clipId)) {
        return false;
      }

      // Upload is done but clip is gone (deleted) — don't show zombie upload card
      if (u.status === UploadStatus.completed) {
        return false;
      }

      return true;
    });
  }, [data?.items, uploads]);

  const filteredClips = useMemo(() => {
    const activeClipIds = new Set(filteredUploads.map((u) => u.clipId));

    return (data?.items ?? []).filter((c) => !activeClipIds.has(c.id));
  }, [data?.items, filteredUploads]);

  const files = useMemo(
    (): FileEntry<C>[] => [
      ...filteredUploads.map(
        (u): FileEntry<C> => ({
          item: u,
          type: GalleryItemType.upload,
        }),
      ),
      ...filteredClips.map(
        (u): FileEntry<C> => ({
          item: u,
          type: GalleryItemType.file,
        }),
      ),
    ],
    [filteredClips, filteredUploads],
  );

  const hiddenClipsCount = (data?.items?.length ?? 0) - filteredClips.length;
  const total = (data?.total ?? 0) - hiddenClipsCount + filteredUploads.length;

  const height = useMemo(() => itemHeight + (itemAdditionalHeight ?? 0), [itemHeight, itemAdditionalHeight]);

  const { boxes: fileBoxes } = useCSSGridLikeLayout({
    height,
    minWidth: itemWidth ?? height * FILE_CARD_ASPECT_RATIO,
    itemCount: files.length,
    containerWidth,
    containerHorizontalPadding,
    itemHorizontalSpacing,
  });

  const renderItem = useCallback(
    (index: number) => {
      const file = files[index];

      if (file) {
        return file.type === GalleryItemType.upload ? (
          <UploadingFileGalleryCard
            width={
              /**
               * Uploads don't inherently have a width and get it from the ratio of the
               * row height
               */
              height
            }
            upload={file.item}
          />
        ) : (
          renderFile?.({
            box: fileBoxes[index],
            data: file.item,
            index,
          })
        );
      }

      return null;
    },
    [files, height, renderFile, fileBoxes],
  );

  const getItemData = useCallback(
    (index: number): GalleryMetadata => {
      const file = files[index];

      return {
        type: file.type,
        isSelectable: file.type === GalleryItemType.file ? isSelectable(file.item) : false,
        itemId: file.item.id,
        canDragTo: (item) => item.type === DragType.file,
      };
    },
    [files, isSelectable],
  );

  const { isHeaderCollapsed, header } = useGallerySectionHeader({
    isFirstSection,
    label: 'Files',
    sectionType: GalleryItemType.file,
    itemCount: total,
    containerWidth,
    containerPadding: containerHorizontalPadding,
    containerHeight: headerContainerHeight,
    renderHeader,
  });

  const footer = useGallerySectionFooter({
    isLoading: !!data?.isLoading,
    loadMore,
    hasMore: !!data?.hasMore,
    containerWidth,
  });

  if (!total && !data?.isLoading) {
    return null;
  }

  return {
    header: total ? header : undefined,
    footer: isHeaderCollapsed ? undefined : footer,
    items: {
      render: renderItem,
      boxes: isHeaderCollapsed ? [] : fileBoxes,
      verticalItemsSpacing:
        rowVerticalSpacing ?? (!isAboveSmallScreen ? VERTICAL_SPACING_SMALL : VERTICAL_SPACING_DEFAULT),
      getData: getItemData,
    },
  };
};
